/**
 * Title overlay for a post image — the single source of truth for how it looks.
 *
 * Three consumers share this module, which is the whole point:
 *   1. the editor (web/src/components/backend/components/Overlay)
 *   2. the post previews (Previews/PicturePreview.jsx)
 *   3. the headless-chrome render service (imagerender/server.js), which burns
 *      the title into a throwaway image at publish time
 *
 * The overlay is a position plus a CSS string. Nothing else.
 *
 * Everything is laid out on a fixed REFERENCE_WIDTH stage and scaled to the
 * output size afterwards. That is what makes a free-text CSS field safe: a
 * `font-size: 64px` means the same thing in a 380px editor preview as in a
 * 1080px published image, because both are the same 1080px stage at a different
 * scale. Position is stored as fractions so dragging is resolution independent.
 */

/** Every layout is computed against this width, then scaled. Never change it. */
export const REFERENCE_WIDTH = 1080;

export const OVERLAY_VERSION = 2;

/** Sensible starting point so a freshly switched-on title already looks right. */
export const DEFAULT_CSS = `font-family: Inter, sans-serif;
font-size: 64px;
font-weight: 800;
line-height: 1.15;
color: #ffffff;
background: rgba(0, 0, 0, 0.55);
padding: 24px 28px;
border-radius: 18px;
text-shadow: 0 2px 12px rgba(0, 0, 0, 0.6);`;

const DEFAULT_BOX = { x: 0.06, y: 0.58, w: 0.88 };

/** Google Fonts we are willing to pull in when the CSS names one. */
export const KNOWN_FONTS = [
  "Inter", "Roboto", "Open Sans", "Montserrat", "Poppins", "Oswald",
  "Playfair Display", "Bebas Neue", "Lato", "Merriweather", "Anton",
  "Archivo Black",
];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export const createDefaultOverlay = () => ({
  version: OVERLAY_VERSION,
  enabled: true,
  box: { ...DEFAULT_BOX },
  // null inherits the default (the group's, else built-in); only an image the
  // user actually customised stores its own CSS
  css: null,
});

const MAX_CSS_LENGTH = 4000;

/**
 * CSS properties a title is allowed to set. Everything else is dropped.
 *
 * A whitelist, not a blacklist. The old filter enumerated dangerous tokens and
 * lost: `image-set("http://…")` and the escape `\75rl(` both reach the network
 * without ever spelling `url(`. Listing what is allowed cannot be out-run that
 * way, because a property nobody listed simply never renders.
 *
 * Deliberately absent: `filter`, `backdrop-filter`, `mask`, `list-style` and
 * `cursor`, all of which take a URL. Geometry (position/left/top/width) is set
 * inline by titleBoxStyle() and stays out of the user's reach on purpose.
 */
export const ALLOWED_PROPERTIES = new Set([
  "font", "font-family", "font-size", "font-style", "font-weight",
  "font-variant", "font-stretch", "font-feature-settings",
  "line-height", "letter-spacing", "word-spacing", "text-indent",
  "color", "opacity", "background", "background-color",
  "padding", "padding-top", "padding-right", "padding-bottom", "padding-left",
  "border", "border-width", "border-style", "border-color", "border-radius",
  "text-align", "text-transform", "text-decoration", "text-decoration-color",
  "text-shadow", "box-shadow", "mix-blend-mode",
  "-webkit-text-stroke", "-webkit-text-stroke-width", "-webkit-text-stroke-color",
]);

/** Value functions that only ever compute locally. Every other one can fetch. */
export const ALLOWED_FUNCTIONS = new Set([
  "rgb", "rgba", "hsl", "hsla", "calc", "min", "max", "clamp",
  "linear-gradient", "radial-gradient",
  "repeating-linear-gradient", "repeating-radial-gradient",
]);

/**
 * Characters a value may consist of. No backslash (CSS escapes rebuild `url(`
 * after any textual filter has run), no colon (`javascript:`), no semicolon,
 * braces or angle brackets (escaping the rule or the <style> element).
 */
const SAFE_VALUE = /^[a-zA-Z0-9\s#%,.\-+*/()'"_]+$/;

/**
 * Every `name(` in a value has to be a function we know stays local. A bare `(`
 * has no name and is rejected too — it is never anything but an attempt to
 * smuggle one past this check.
 */
const functionsAreAllowed = (value) => {
  if ((value.match(/\(/g) || []).length !== (value.match(/\)/g) || []).length) {
    return false;
  }

  for (const [, name] of value.matchAll(/([a-zA-Z_-]*)\s*\(/g)) {
    if (!ALLOWED_FUNCTIONS.has(name.toLowerCase())) return false;
  }

  return true;
};

/**
 * Reduces free-text CSS to the declarations we are willing to render.
 *
 * The CSS is authored in the browser and then applied inside a headless Chrome
 * we control, so this is a real boundary, not cosmetics. It is defence in depth
 * on top of the request blocking in imagerender/server.js, never instead of it:
 * anything that still finds a way to name a URL here is refused at the socket.
 *
 * Mirrored by OverlaySanitizer::cleanCss() in the API — keep the two lists in
 * sync or the editor preview stops matching the published image.
 */
export const sanitizeCss = (css) => {
  if (typeof css !== "string") return "";

  const declarations = [];

  for (const raw of css.slice(0, MAX_CSS_LENGTH).split(";")) {
    const declaration = raw.trim();
    if (declaration === "") continue;

    const colon = declaration.indexOf(":");
    if (colon < 1) continue;

    const property = declaration.slice(0, colon).trim().toLowerCase();
    const value = declaration.slice(colon + 1).trim();

    if (!ALLOWED_PROPERTIES.has(property)) continue;
    if (value === "" || !SAFE_VALUE.test(value)) continue;
    if (!functionsAreAllowed(value)) continue;

    declarations.push(`${property}: ${value}`);
  }

  return declarations.length === 0 ? "" : declarations.join(";\n") + ";";
};

/**
 * Accepts anything that has ever been in the `overlay` column and returns a
 * current-version config.
 *
 * A missing overlay is an *enabled* default, not "off" — the title is on by
 * default, so an image nobody has touched still gets one.
 */
export const normalizeOverlay = (raw) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return createDefaultOverlay();
  }

  // Version 1 kept a layer array with structured styles. Only the position is
  // worth carrying across; the styling is replaced by the CSS field.
  let box = raw.box;
  if (!box && raw.layouts) {
    const layers = raw.layouts?.base?.layers || [];
    box = layers.find((layer) => layer.type === "text")?.box;
  }
  box = box || DEFAULT_BOX;

  return {
    version: OVERLAY_VERSION,
    enabled: raw.enabled === undefined ? true : Boolean(raw.enabled),
    box: {
      x: clamp(Number(box.x) || 0, -0.5, 1.5),
      y: clamp(Number(box.y) || 0, -0.5, 1.5),
      w: clamp(Number(box.w) || DEFAULT_BOX.w, 0.05, 1.5),
    },
    css: typeof raw.css === "string" && raw.css.trim() !== "" ? sanitizeCss(raw.css) : null,
  };
};

export const setOverlayEnabled = (overlay, enabled) => ({
  ...normalizeOverlay(overlay),
  enabled: Boolean(enabled),
});

/**
 * The CSS a title is actually drawn with: its own, else the inherited default
 * (the group's, sent by the API with the file list), else the built-in style.
 */
export const effectiveCss = (overlay, defaultCss) =>
  normalizeOverlay(overlay).css ||
  (defaultCss && String(defaultCss).trim()) ||
  DEFAULT_CSS;

/** Whether a title should actually be drawn: switched on and some text to show. */
export const isOverlayActive = (overlay, title) =>
  normalizeOverlay(overlay).enabled && String(title || "").trim() !== "";

/**
 * Overlays are an image-only feature; video would need a different renderer.
 * Documents do not always carry a mime type, so the filename is the fallback.
 */
export const isImageDocument = (document) => {
  if (!document) return false;
  if (document.mime_type) {
    return String(document.mime_type).startsWith("image/");
  }

  return /\.(jpe?g|png|gif|webp|bmp|tiff?)$/i.test(
    String(document.original_name || "")
  );
};

/** Stage height for an image, in reference pixels. */
export const stageHeight = (imageWidth, imageHeight) => {
  if (!imageWidth || !imageHeight) return REFERENCE_WIDTH;

  return Math.round(REFERENCE_WIDTH * (imageHeight / imageWidth));
};

/**
 * Position of the title box on the stage. Only geometry lives here — everything
 * visual comes from the user's CSS, so the two can never fight.
 */
export const titleBoxStyle = (overlay) => {
  const { box } = normalizeOverlay(overlay);

  return {
    position: "absolute",
    left: `${box.x * 100}%`,
    top: `${box.y * 100}%`,
    width: `${box.w * 100}%`,
    boxSizing: "border-box",
    margin: 0,
    whiteSpace: "pre-wrap",
    overflowWrap: "break-word",
    wordBreak: "break-word",
  };
};

/** <link> for the Google Fonts actually named in the CSS. */
export const googleFontsHref = (css) => {
  const text = String(css || "");
  const families = KNOWN_FONTS.filter((family) =>
    new RegExp(family.replace(/ /g, "\\s+"), "i").test(text)
  );
  if (families.length === 0) return null;

  const params = families
    .map(
      (family) =>
        `family=${encodeURIComponent(family).replace(/%20/g, "+")}:wght@400;600;700;800;900`
    )
    .join("&");

  return `https://fonts.googleapis.com/css2?${params}&display=swap`;
};

const escapeHtml = (value) =>
  String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const styleToInline = (style) =>
  Object.keys(style)
    .map((key) => {
      const name = key.replace(/[A-Z]/g, (m) => "-" + m.toLowerCase());
      return `${name}:${style[key]}`;
    })
    .join(";");

/**
 * Standalone HTML the headless renderer screenshots.
 *
 * The stage is always REFERENCE_WIDTH wide; the caller scales the screenshot up
 * to the real image size, so the CSS behaves identically at every output size.
 *
 * @param {object} overlay
 * @param {object} options  imageSrc (data URI), imageWidth, imageHeight, title,
 *                          defaultCss (inherited CSS, optional)
 */
export const buildOverlayHtml = (overlay, options) => {
  const { imageSrc, imageWidth, imageHeight, title, defaultCss } = options;
  const config = normalizeOverlay(overlay);
  const height = stageHeight(imageWidth, imageHeight);
  const text = String(title || "").trim();
  const show = config.enabled && text !== "";
  const css = effectiveCss(config, defaultCss);
  const fontsHref = show ? googleFontsHref(css) : null;

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
${fontsHref ? `<link rel="stylesheet" href="${fontsHref}" />` : ""}
<style>
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: transparent; }
  #ov-stage { position: relative; width: ${REFERENCE_WIDTH}px; height: ${height}px; overflow: hidden; }
  #ov-base { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
  /* The user's CSS, scoped to the title box so it cannot touch anything else. */
  #ov-title { ${sanitizeCss(css)} }
</style>
</head>
<body>
<div id="ov-stage">
  <!-- Escaped: imageSrc is built from the caller's mimeType and image bytes, and
       an unescaped quote there would close the attribute and inject markup. -->
  <img id="ov-base" src="${escapeHtml(imageSrc)}" alt="" />
  ${show ? `<div id="ov-title" style="${styleToInline(titleBoxStyle(config))}">${escapeHtml(text)}</div>` : ""}
</div>
</body>
</html>`;
};

const overlayRenderer = {
  REFERENCE_WIDTH,
  OVERLAY_VERSION,
  DEFAULT_CSS,
  KNOWN_FONTS,
  createDefaultOverlay,
  normalizeOverlay,
  setOverlayEnabled,
  effectiveCss,
  isOverlayActive,
  isImageDocument,
  sanitizeCss,
  stageHeight,
  titleBoxStyle,
  googleFontsHref,
  buildOverlayHtml,
};

export default overlayRenderer;
